import { useEffect, useMemo, useState } from 'react';

import { PaginationState } from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';

import { DocChronRow } from './components/doc-chron-row';
import { DocChronRowSkeleton } from './components/doc-chron-row-skeleton';
import { Permission } from '../../../../../../../../../../backend/src/auth/permissions/permission';
import {
  DocChronologyDownloadColumn,
  DocChronologyDownloadColumnOptions,
} from '../../../../../../../../../../backend/src/matter/subset/chronology/doc-chronology/doc-chronology-download';
import { DownloadFormat, StyleFormat } from '../../../../../../../../../../backend/src/types/types';
import { ExportType } from '../../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { APIBaseChronos } from '@/api/hosts';
import { useCreateDocumentZip } from '@/api/mutations/useCreateDocumentZip';
import useDocChronologyDownload from '@/api/mutations/useDownloadDocChronology';
import useExportBundle from '@/api/mutations/useExportBundle';
import { useUpdateDocChronology } from '@/api/mutations/useUpdateDocChronologyTitle';
import { useGetDocChronology } from '@/api/queries/useGetDocChronology';
import { useGetDocChronologyBundleItems } from '@/api/queries/useGetDocChronologyBundleItems';
import { useGetDocChronologyDocs } from '@/api/queries/useGetDocChronologyDocs';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetFilterOptions } from '@/api/queries/useGetMetadataFilterOptions';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useUserContext } from '@/Contexts/User';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { DownloadFlow } from '@/new/components/download/DownloadFlow';
import { LARGE_BUNDLE_BLOCK_THRESHOLD } from '@/new/components/download/fields/large-export-warning';
import type {
  AsyncVariant,
  BundleVariant,
  DownloadDefinition,
  DownloadVariant,
  ZipOption,
} from '@/new/components/download/types';
import { useDownloadController } from '@/new/components/download/useDownloadController';
import { EditFieldDialog } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Pagination } from '@/new/components/pagination';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Skeleton } from '@/new/components/skeleton';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';

function useDocChronologyBundleSeeds({
  matterId,
  subsetId,
  docChronologyId,
}: {
  matterId: string;
  subsetId: string;
  docChronologyId: string;
}) {
  const { data, isLoading, isError } = useGetDocChronologyBundleItems({
    matterId,
    subsetId,
    docChronologyId,
    enabled: true,
  });
  const items = (data ?? []).map((entry) => ({
    id: entry.docId,
    schemaName: 'doc' as const,
    schemaId: entry.docId,
    originalName: entry.title,
    pageCount: entry.pageCount ?? undefined,
    dateText: entry.dateText,
  }));
  return { items, isLoading, isError };
}

export function DocChronology() {
  const { matterId, subsetId, docChronologyId } = useParams();

  if (!matterId || !subsetId || !docChronologyId) return null;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Doc Chronologies', path: 'chronologies/doc' },
  });

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const download = useDownloadController();

  const { downloadMutation } = useDocChronologyDownload({ docChronologyId, matterId, subsetId });
  const { bundleEndpoints } = useExportBundle();
  const { mutateAsync: fetchDownload } = downloadMutation;
  const { createZipAsync } = useCreateDocumentZip({ matterId, subsetId });

  const { data: docChronology } = useGetDocChronology({ matterId, subsetId, docChronologyId });
  const docTitle = docChronology?.title ?? 'Document Chronology';

  const { mutate: updateDocChronology } = useUpdateDocChronology();
  const { user } = useUserContext();
  const isCreator = docChronology?.createdById === user?.id;

  const [localSelectedRelativityFields, setLocalSelectedRelativityFields] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (docChronology?.relativityFields) {
      setLocalSelectedRelativityFields(new Set(docChronology.relativityFields));
    }
  }, [docChronology?.relativityFields]);

  const {
    data,
    isLoading: isLoadingDocs,
    isPreviousData,
  } = useGetDocChronologyDocs({ matterId, subsetId, docChronologyId, page: pageIndex + 1, pageSize });

  const { data: filterOptions } = useGetFilterOptions({ matterId });

  const goToPage = (val: number) => setPagination({ pageIndex: val, pageSize });
  const prevPage = () => goToPage(pageIndex - 1);
  const nextPage = () => goToPage(pageIndex + 1);
  const totalPages = data?.pageCount;

  const downloadDefinition = useMemo<DownloadDefinition>(() => {
    const progressEndpoint = () =>
      `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology/${docChronologyId}/export-progress`;
    const statusEndpoint = () =>
      `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/doc-chronology/${docChronologyId}/export-status`;

    const tableVariant: AsyncVariant<DownloadFormat> = {
      id: 'doc-chronology-table',
      kind: 'table',
      label: 'Table',
      description: 'Word or Excel',
      mode: 'async',
      formats: [DownloadFormat.EXCEL, DownloadFormat.DOCX],
      defaultFilename: docTitle,
      columns: {
        available: DocChronologyDownloadColumnOptions as string[],
        default: DocChronologyDownloadColumnOptions as string[],
        relativity: filterOptions?.relativityFields?.fields,
      },
      sourceDocs: true,
      largeExportWarning: true,
      mutate: async (values) =>
        fetchDownload({
          title: values.filename,
          columns: (values.columns ?? []) as DocChronologyDownloadColumn[],
          relativityColumns: values.relativityColumns,
          styleFormat: StyleFormat.Table,
          downloadFormat: values.format,
          includeSourceDocuments: values.includeSourceDocuments,
          orderByDate: values.orderByDate,
        }),
      progressEndpoint,
      statusEndpoint,
    };

    const writtenVariant: AsyncVariant<DownloadFormat> = {
      id: 'doc-chronology-written',
      kind: 'written',
      label: 'Written',
      description: 'Word',
      mode: 'async',
      formats: [DownloadFormat.DOCX],
      defaultFilename: docTitle,
      style: true,
      styleItemNoun: 'document',
      sourceDocs: true,
      largeExportWarning: true,
      mutate: async (values) =>
        fetchDownload({
          title: values.filename,
          style: values.style,
          styleFormat: StyleFormat.Written,
          downloadFormat: values.format,
          documentOptions: values.docOptions,
          includeSourceDocuments: values.includeSourceDocuments,
          orderByDate: values.orderByDate,
        }),
      progressEndpoint,
      statusEndpoint,
    };

    const zipProgressEndpoint = () => `${APIBaseChronos}/api/matter/${matterId}/docs/zip-progress/${subsetId}`;
    const zipStatusEndpoint = () => `${APIBaseChronos}/api/matter/${matterId}/docs/zip-status/${subsetId}`;

    const zipVariant: AsyncVariant<'zip'> = {
      id: 'doc-chronology-zip',
      kind: 'zip',
      label: 'Zip',
      description: 'Archive of selected documents from this chronology.',
      mode: 'async',
      formats: ['zip'],
      defaultFilename: docTitle,
      showZipContent: true,
      mutate: async (values: { zipContent?: ZipOption; filename: string }) => {
        const data = await createZipAsync({
          docChronologyId,
          zipContent: values.zipContent,
          title: values.filename,
        });
        if (!data.exportId) throw new Error('Failed to start zip creation');
        return { exportId: data.exportId, jobId: data.jobId };
      },
      progressEndpoint: zipProgressEndpoint,
      statusEndpoint: zipStatusEndpoint,
      readyDescription: 'Source documents have been packaged as a zip.',
    };

    const chronologyDocCount = data?.totalCount ?? 0;
    const bundleVariant: BundleVariant = {
      id: 'doc-chronology-bundle',
      kind: 'bundle',
      label: 'PDF Bundle',
      description: 'Paginated PDFs from Document Chronology',
      disabledReason:
        chronologyDocCount > LARGE_BUNDLE_BLOCK_THRESHOLD
          ? `Bundles are limited to ${LARGE_BUNDLE_BLOCK_THRESHOLD.toLocaleString()} documents (this chronology has ${chronologyDocCount.toLocaleString()}).`
          : undefined,
      bundle: {
        matterId,
        subsetId,
        itemType: 'doc',
        useItems: () => useDocChronologyBundleSeeds({ matterId, subsetId, docChronologyId }),
        defaultName: `${docTitle} Bundle`,
      },
      ...bundleEndpoints(matterId, subsetId),
      readyDescription: 'Your PDF bundle is ready.',
    };

    const variants: DownloadVariant[] = [tableVariant, writtenVariant, zipVariant, bundleVariant];
    return {
      totalCount: data?.totalCount,
      variants,
      tracking: {
        event: TrackingEventsV2.DocChronologyDownload,
        basePayload: { matterId, subsetId, docChronologyId },
      },
    };
  }, [
    matterId,
    subsetId,
    docChronologyId,
    docTitle,
    data?.totalCount,
    filterOptions?.relativityFields?.fields,
    fetchDownload,
    bundleEndpoints,
    createZipAsync,
  ]);

  return (
    <>
      <Breadcrumb
        items={[
          ...breadcrumbItems.map((item, index) =>
            index === breadcrumbItems.length - 1 ? { ...item, active: false } : item,
          ),
          {
            href: `/matters/${matterId}/subsets/${subsetId}/chronologies/doc/${docChronologyId}`,
            label: docTitle,
            active: true,
          },
        ]}
      />

      <PageContainer>
        <PageHeader>
          {docChronology ? (
            <div className="flex items-center gap-3.5">
              <PageTitle text={docChronology.title} iconClass="lucide--folder-git-2" />
              <PermissionGate permission={Permission.DocChronologyEdit} orAllowed={isCreator}>
                <EditFieldDialog
                  triggerClassName="text-base"
                  currentValue={docChronology.title}
                  onSave={(title) => updateDocChronology({ matterId, subsetId, docChronologyId, title })}
                  title="Update Document Chronology"
                  description="Change the title of this document chronology."
                  fieldLabel="Chronology Title"
                  placeholder="Emails between 2010 and 2016..."
                />
              </PermissionGate>
            </div>
          ) : (
            <Skeleton className="h-10 w-36" />
          )}

          <div className="flex items-center gap-2">
            <PermissionGate permission={Permission.DocChronologyDownload}>
              <Button disabled={!isLoadingDocs && data?.totalCount === 0} onClick={download.open}>
                <Icon className="lucide--download" />
                Download
              </Button>
              <DownloadFlow controller={download} definition={downloadDefinition} />
            </PermissionGate>
            <Pagination
              canGetPrevPage={pageIndex > 0}
              canGetNextPage={pageIndex < (totalPages ?? 0) - 1}
              prevPage={prevPage}
              nextPage={nextPage}
              currentPage={pageIndex}
              noOfPages={totalPages}
              goToPage={goToPage}
            />
          </div>
        </PageHeader>

        <div className={`${isPreviousData ? 'opacity-60' : 'opacity-100'} relative pt-1 pl-1 duration-150`}>
          <div className="absolute -top-2.5 left-2.5 z-5 h-18 w-2 bg-white"></div>

          {isLoadingDocs ? (
            <>
              {[...Array(4)].map((_, index) => (
                <div key={index} className="group relative mb-6 flex w-full pl-8.5">
                  <div className="absolute top-11.5 left-0.25 z-10 h-4.25 w-4.25 rounded-full border-2 border-gray-200/80 bg-gray-50" />
                  <DocChronRowSkeleton />
                </div>
              ))}
            </>
          ) : !data?.docs?.length ? (
            <div className="flex w-full items-center justify-center py-12">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Icon className="lucide--folder-git-2" />
                  </EmptyMedia>
                  <EmptyTitle>This chronology has no documents</EmptyTitle>
                  <EmptyDescription className="text-wrap">
                    This document chronology does not contain any documents.{' '}
                    <Link
                      to={`/matters/${matterId}/subsets/${subsetId}/docs`}
                      className="text-sm text-blue-500 hover:underline"
                    >
                      Add more documents
                    </Link>
                    .
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          ) : (
            data?.docs?.map((doc) => (
              <div key={doc.id} className="group relative mb-6 flex w-full pl-8.5">
                <div className="absolute top-11.5 left-0.25 z-10 h-4.25 w-4.25 rounded-full border-2 border-teal-400 bg-teal-50" />
                <div className="absolute inset-0 -inset-y-3 w-px translate-x-[9px] bg-gray-200 group-first:top-11.5 group-last:bottom-[calc(100%-46px)]"></div>

                <DocChronRow
                  chronDoc={doc}
                  docChronology={docChronology}
                  localSelectedRelativityFields={localSelectedRelativityFields}
                  setLocalSelectedRelativityFields={setLocalSelectedRelativityFields}
                  isCreator={isCreator}
                />
              </div>
            ))
          )}
        </div>
      </PageContainer>
    </>
  );
}
